import { Env } from '@magicdoorinc/env';
import { useLocalization, IconLoader, PayabliCardForm, IconLock, PayabliMode } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, Show, untrack } from 'solid-js';
import { useCurrentCompany, usePayPaymentMethods } from '~/contexts/global';
import { PaymentMethodSetupMode, PaymentMethodType } from '~/swagger/pay/Api';
import { getEnv } from '~/utils/env';
import type { PayabliCardFormInstance, PayabliSuccessResponse } from '@magicdoorinc/ui';
import type { PaymentMethodDto } from '~/swagger/pay/Api';

export type PayabliCardSetupPanelProps = {
  active: boolean;
  onSuccess?: (result: PaymentMethodDto) => void;
  onRegisterAddAction?: (run: () => void) => void;
};

export const PayabliCardSetupPanel = (props: PayabliCardSetupPanelProps) => {
  const { t } = useLocalization();
  const { currentCompany } = useCurrentCompany();
  const { setupConfig: paySetupConfig, setSetupParams, createPaymentMethod } = usePayPaymentMethods();

  let payabliInstRef: PayabliCardFormInstance | null = null;
  const [payabliInstVersion, setPayabliInstVersion] = createSignal(0);

  const companyId = createMemo(() => currentCompany()?.id);

  createEffect(() => {
    const id = companyId();
    if (props.active && id != null) {
      setSetupParams({ mode: PaymentMethodSetupMode.Card });
    }
  });

  createEffect(() => {
    payabliInstVersion();
    untrack(() => {
      props.onRegisterAddAction?.(() => payabliInstRef?.payabliExec('method'));
    });
  });

  const setupConfig = createMemo(() => paySetupConfig());
  const isConfigReady = createMemo(() => setupConfig()?.publicToken != null);

  const onPayabliSuccess = async (response: PayabliSuccessResponse) => {
    if (response.responseText !== 'Success' || !response.responseData?.referenceId) return;

    if (companyId() == null) return;

    try {
      const result = await createPaymentMethod({
        source: 'token',
        type: PaymentMethodType.Card,
        token: response.responseData.referenceId,
      });
      props.onSuccess?.(result);
    } catch {
      payabliInstRef?.payabliExec('reinit');
    }
  };

  return (
    <div class="flex flex-col gap-4 p-6">
      <Show
        when={isConfigReady()}
        fallback={
          <div class="flex flex-col items-center justify-center py-12">
            <IconLoader class="mb-4 size-8 animate-spin text-primary" />
            <p class="text-sm text-gray-600">{t('Loading secure payment form...')}</p>
          </div>
        }>
        <div>
          <PayabliCardForm
            ref={(inst) => {
              payabliInstRef = inst;
              setPayabliInstVersion((v) => v + 1);
            }}
            config={{
              token: setupConfig()!.publicToken,
              entryPoint: setupConfig()?.metadata?.entryPoint,
              mode: getEnv() === Env.Production ? PayabliMode.Production : PayabliMode.Sandbox,
              functionCallBackSuccess: onPayabliSuccess,
            }}
            class="p-0"
          />
        </div>
      </Show>

      <div class="rounded-lg border border-gray-200 p-4">
        <div class="flex items-start gap-3">
          <IconLock class="mt-0.5 size-5 shrink-0" />
          <div class="text-sm">
            <p class="mb-1 font-medium">{t('Secure payment processing')}</p>
            <p class="text-gray-600">{t('Your payment information is encrypted and secure. We never store your full card details')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
